// src/components/screener/NLSearchBar.jsx
// New file — Natural Language search SHELL. Deliberately does not attempt real query
// translation yet — that's a genuinely bigger architecture decision (see the conversation this
// was scoped in: structured-intent extraction vs. full semantic interpretation, cost/latency of
// an LLM round-trip per search, and the real gap that "AI stocks"-style theme queries need data
// that doesn't exist yet — Theme is 'stub' in the field registry).
//
// What this shell DOES do, and why that's still valuable on its own:
//   1. Captures the raw query text as a genuine Tier 3 D3 signal (nl_search_query) — this is
//      arguably the richest signal in the whole product: literally what a member is trying to
//      find, in their own words, before any translation logic exists to act on it. Building the
//      capture path now means zero signal history is lost while the translation logic gets
//      designed properly in its own session.
//   2. Sets honest expectations — tells the member plainly that this searches nothing yet,
//      rather than silently returning unfiltered results or a fake-confident wrong answer.
//
// This is intentionally NOT hidden or deprioritized — full-width, above the preset scan
// buttons, per the owner's explicit placement request, so the roadmap commitment is visible in
// the product itself, not just in a session file that could get missed again.

import React, { useState } from 'react';
import { S, API_URL } from '../../constants/screenerConfig';

export default function NLSearchBar() {
  const [query, setQuery]     = useState('');
  const [status, setStatus]   = useState('idle'); // idle | sending | sent
  const [message, setMessage] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!query.trim() || status === 'sending') return;
    setStatus('sending');
    try {
      const res  = await fetch(`${API_URL}/api/screener/nl-search`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ query: query.trim() }),
      });
      const data = await res.json();
      setMessage(data.message || "Natural language search isn't live yet — your search was saved so we build the right thing when it ships.");
      setStatus('sent');
    } catch (e) {
      setMessage("Couldn't reach the server, but no harm done — this feature isn't live yet anyway.");
      setStatus('sent');
    }
  }

  return (
    <div style={{
      marginBottom: 16, padding: '14px 16px', borderRadius: 10,
      background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.05))',
      border: '1px solid rgba(99,102,241,0.25)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: S.text }}>🔍 Describe what you're looking for</span>
        <span style={{
          fontSize: 9, padding: '2px 8px', borderRadius: 10, fontWeight: 700,
          background: 'rgba(245,158,11,0.15)', color: S.amber,
        }}>
          COMING SOON
        </span>
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8 }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="e.g. best return last month, excluding OTC — try it, we're listening"
          maxLength={280}
          style={{
            flex: 1, padding: '9px 14px', borderRadius: 8, fontSize: 13,
            background: S.bg, color: S.text, border: `1px solid ${S.border2}`,
            fontFamily: 'inherit', outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={!query.trim() || status === 'sending'}
          style={{
            padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
            background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
            border: 'none', cursor: status === 'sending' ? 'wait' : 'pointer', fontFamily: 'inherit',
            opacity: !query.trim() ? 0.5 : 1,
          }}
        >
          {status === 'sending' ? 'Sending…' : 'Search'}
        </button>
      </form>
      {status === 'sent' && (
        <div style={{ fontSize: 12, color: S.muted, marginTop: 8 }}>{message}</div>
      )}
      <div style={{ fontSize: 11, color: S.subtle, marginTop: 8 }}>
        We're building this to actually translate plain-English requests into real filters —
        every search you try here helps us build it right. Use the filters and Proven Screens above in the meantime.
      </div>
    </div>
  );
}
